// src/pages/OfflinePage.jsx
// ══════════════════════════════════════════════════════════════════════════════
// KAILUX — OFFLINE SHELL PAGE  (Phase A2)
// ══════════════════════════════════════════════════════════════════════════════
// Served by the service worker when the user navigates to any route while
// completely offline. Also accessible at /offline for direct linking.
// Shows cached client list and AFS history from localStorage snapshot.
// ══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import Icon from '../components/Icon'

import { T, alpha } from '../theme/theme'
// ── Local snapshot helpers ────────────────────────────────────────────────────
// main.jsx / api layer writes these on successful fetches so we have stale data
const SNAPSHOT_KEYS = {
  clients:    'kailux_offline_clients',
  afsHistory: 'kailux_offline_afs_history',
  firmName:   'kailux_offline_firm_name',
}

function readSnapshot(key) {
  try {
    const raw = localStorage.getItem(key)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

export function writeOfflineSnapshot(key, data) {
  try {
    localStorage.setItem(key, JSON.stringify(data))
  } catch {
    // Storage full — ignore
  }
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function OfflinePage() {
  const navigate  = useNavigate()
  const [online,   setOnline]   = useState(navigator.onLine)
  const [checking, setChecking] = useState(false)
  const [clients,  setClients]  = useState([])
  const [history,  setHistory]  = useState([])
  const firmName = readSnapshot(SNAPSHOT_KEYS.firmName) || 'Your Firm'

  useEffect(() => {
    setClients(readSnapshot(SNAPSHOT_KEYS.clients)  || [])
    setHistory(readSnapshot(SNAPSHOT_KEYS.afsHistory) || [])

    const handleOnline  = () => { setOnline(true)  }
    const handleOffline = () => { setOnline(false) }
    window.addEventListener('online',  handleOnline)
    window.addEventListener('offline', handleOffline)
    return () => {
      window.removeEventListener('online',  handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  // Auto-redirect when connection returns
  useEffect(() => {
    if (online) {
      const timer = setTimeout(() => navigate('/dashboard'), 1500)
      return () => clearTimeout(timer)
    }
  }, [online, navigate])

  const handleRetry = async () => {
    setChecking(true)
    try {
      await fetch('/health', { cache: 'no-store', signal: AbortSignal.timeout(5000) })
      navigate('/dashboard')
    } catch {
      setChecking(false)
    }
  }

  return (
    <div style={{ minHeight: '100vh', background: T.bg,
      fontFamily: "'Outfit', sans-serif", color: T.tx }}>

      {/* Header */}
      <div style={{ background: T.s1, borderBottom: `1px solid ${T.bd}`,
        padding: '0 28px', height: '62px', display: 'flex',
        alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontFamily: "'Syne', sans-serif", fontSize: '20px', fontWeight: '800' }}>
          Kai<span style={{ color: T.or }}>Lux</span>
        </div>
        <span style={{ fontSize: '10px', padding: '3px 10px', borderRadius:'6px',
          background: alpha(T.rd, 0.12), border: `1px solid ${alpha(T.rd, 0.3)}`,
          color: T.rd, fontWeight: '700', letterSpacing: '1px' }}>
          <Icon name="offline" /> OFFLINE
        </span>
      </div>

      <div style={{ maxWidth: '640px', margin: '0 auto', padding: '48px 24px' }}>

        {/* Status card */}
        <div style={{ background: T.s1, border: `1px solid ${T.bd}`,
          borderRadius:'16px', padding:'32px', textAlign: 'center',
          marginBottom: '28px' }}>

          <div style={{ fontSize: '52px', marginBottom: '16px' }}>
            {online ? <Icon name="checkCircle" strokeWidth={1.5} style={{ color: T.gn }} /> : <Icon name="offline" strokeWidth={1.5} style={{ color: T.rd }} />}
          </div>

          {online ? (
            <>
              <div style={{ fontFamily: "'Syne', sans-serif", fontSize: '20px',
                fontWeight: '800', color: T.gn, marginBottom: '8px' }}>
                Connection restored!
              </div>
              <div style={{ fontSize: '14px', color: T.mt, marginBottom: '20px' }}>
                Redirecting you to the dashboard...
              </div>
              <div style={{ width: '48px', height: '48px', border: `3px solid ${alpha(T.gn, 0.2)}`,
                borderTopColor: T.gn, borderRadius: '50%', margin: '0 auto',
                animation: 'spin 0.8s linear infinite' }}/>
            </>
          ) : (
            <>
              <div style={{ fontFamily: "'Syne', sans-serif", fontSize: '20px',
                fontWeight: '800', marginBottom: '8px' }}>
                No internet connection
              </div>
              <div style={{ fontSize: '14px', color: T.mt, lineHeight: '1.7',
                marginBottom: '24px', maxWidth: '400px', margin: '0 auto 24px' }}>
                KaiLux needs an internet connection to generate financial statements
                and sync your data. Your existing data is safely stored.
              </div>
              <button
                onClick={handleRetry}
                disabled={checking}
                style={{ padding: '12px 28px', borderRadius:'8px', border: 'none',
                  background: checking
                    ? T.mt
                    : T.prg,
                  color:T.onpri, fontFamily: "'Outfit', sans-serif",
                  fontSize: '14px', fontWeight: '700',
                  cursor: checking ? 'not-allowed' : 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap:'8px' }}>
                {checking ? (
                  <>
                    <span style={{ width: '14px', height: '14px',
                      border: `2px solid ${alpha(T.onpri, 0.3)}`,
                      borderTopColor:T.onpri, borderRadius: '50%',
                      animation: 'spin 0.8s linear infinite', display: 'inline-block' }}/>
                    Checking...
                  </>
                ) : '↻ Retry Connection'}
              </button>
            </>
          )}
        </div>

        {/* Cached clients */}
        {clients.length > 0 && (
          <div style={{ background: T.s1, border: `1px solid ${T.bd}`,
            borderRadius:'12px', overflow: 'hidden', marginBottom: '16px' }}>
            <div style={{ padding: '14px 18px', borderBottom: `1px solid ${T.bd}`,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize:'13px', fontWeight: '700', color: T.tx }}>
                Client Portfolio
              </div>
              <span style={{ fontSize: '10px', color: T.mt }}>
                Cached · {clients.length} client{clients.length !== 1 ? 's' : ''}
              </span>
            </div>
            {clients.slice(0, 6).map((client, i) => (
              <div key={client.id || i}
                style={{ padding: '12px 18px',
                  borderBottom: i < Math.min(clients.length, 6) - 1
                    ? `1px solid ${T.bd}` : 'none',
                  display: 'flex', alignItems: 'center', gap:'12px',
                  opacity: 0.75 }}>
                <div style={{ width: '32px', height: '32px', borderRadius:'8px',
                  background: T.ord, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', fontSize:'14px', fontWeight: '800',
                  color: T.or, flexShrink: 0 }}>
                  {(client.name || '?')[0]}
                </div>
                <div>
                  <div style={{ fontSize:'14px', fontWeight: '600' }}>{client.name}</div>
                  <div style={{ fontSize:'12px', color: T.mt }}>
                    {client.entity_type} · Year end: {client.year_end_month}
                  </div>
                </div>
              </div>
            ))}
            {clients.length > 6 && (
              <div style={{ padding: '10px 18px', fontSize:'12px', color: T.mt,
                textAlign: 'center', borderTop: `1px solid ${T.bd}` }}>
                +{clients.length - 6} more clients
              </div>
            )}
          </div>
        )}

        {/* Cached AFS history */}
        {history.length > 0 && (
          <div style={{ background: T.s1, border: `1px solid ${T.bd}`,
            borderRadius:'12px', overflow: 'hidden', marginBottom: '16px' }}>
            <div style={{ padding: '14px 18px', borderBottom: `1px solid ${T.bd}`,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize:'13px', fontWeight: '700', color: T.tx }}>
                Recent AFS Jobs
              </div>
              <span style={{ fontSize: '10px', color: T.mt }}>Cached</span>
            </div>
            {history.slice(0, 5).map((job, i) => (
              <div key={job.id || i}
                style={{ padding: '12px 18px',
                  borderBottom: i < Math.min(history.length, 5) - 1
                    ? `1px solid ${T.bd}` : 'none',
                  display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', opacity: 0.75 }}>
                <div>
                  <div style={{ fontSize:'13px', fontWeight: '600' }}>
                    {job.clients?.name || '—'}
                  </div>
                  <div style={{ fontSize: '10px', color: T.mt }}>
                    {job.financial_year} · {job.engagement_type}
                  </div>
                </div>
                <span style={{ fontSize: '9px', padding: '2px 8px', borderRadius:'6px',
                  background: job.status === 'complete' ? T.gnd : T.ord,
                  color: job.status === 'complete' ? T.gn : T.or,
                  fontWeight: '700' }}>
                  {job.status === 'complete' ? 'DONE' : 'IN PROGRESS'}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* What works offline */}
        <div style={{ background: T.s2, border: `1px solid ${T.bd}`,
          borderRadius:'12px', padding: '18px 20px' }}>
          <div style={{ fontSize:'12px', fontWeight: '700', color: T.mt,
            letterSpacing: '1px', marginBottom: '12px' }}>
            WHAT WORKS OFFLINE
          </div>
          {[
            [<Icon name="check" />, T.gn, 'Viewing cached client list and AFS history'],
            [<Icon name="check" />, T.gn, 'Browsing previously generated PDF statements (if downloaded)'],
            [<Icon name="x" />, T.rd, 'Uploading new trial balances or bank statements'],
            [<Icon name="x" />, T.rd, 'AI mapping and note generation'],
            [<Icon name="x" />, T.rd, 'Generating new AFS PDFs'],
            [<Icon name="x" />, T.rd, 'Director sign-off portal'],
          ].map(([icon, color, text], i) => (
            <div key={i} style={{ display: 'flex', gap:'8px', alignItems: 'center',
              padding: '6px 0', borderBottom: i < 5 ? `1px solid ${T.bd}` : 'none',
              fontSize:'13px' }}>
              <span style={{ color, fontWeight: '800', flexShrink: 0 }}>{icon}</span>
              <span style={{ color: T.mt }}>{text}</span>
            </div>
          ))}
        </div>

        <div style={{ textAlign: 'center', marginTop: '24px',
          fontSize:'13px', color: T.mt, lineHeight: '1.6' }}>
          KaiLux · {firmName} · IFRS for SMEs
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
    </div>
  )
}
